import { ChangeOrderDocument, ChangeOrderStatus } from 'types';

// Admin-facing banner label per change-order status. Terminal-resolved states (executed,
// cancelled, voided) map to nothing — the CO is done and the banner clears. Declined and Expired
// intentionally DO map to a label: they persist until a newer CO supersedes them (the "most
// relevant" CO below is always the latest in the chain).
const CHANGE_ORDER_BANNER_LABELS: Partial<Record<ChangeOrderStatus, string>> = {
  [ChangeOrderStatus.created]: 'Change Order Sent',
  [ChangeOrderStatus.sent]: 'Change Order Sent',
  [ChangeOrderStatus.viewed]: 'Change Order Viewed',
  [ChangeOrderStatus.awaitingDisclosure]: 'Awaiting Illinois Shines Disclosure',
  [ChangeOrderStatus.partiallyAccepted]: 'Change Order Partial Acceptance',
  [ChangeOrderStatus.declined]: 'Change Order Declined',
  [ChangeOrderStatus.expired]: 'Change Order Expired',
};

// The most relevant CO is the latest in the addendum chain (highest sequence). Looking only at the
// latest CO gives the "superseded by a new CO" behavior for free: an older declined/expired CO is
// resolved the moment a newer CO exists, so it stops driving the banner.
export const getMostRelevantChangeOrder = (
  changeOrders: ChangeOrderDocument[] | undefined,
): ChangeOrderDocument | undefined => {
  if (!changeOrders?.length) return undefined;
  return changeOrders.reduce((latest, co) => (co.sequence > latest.sequence ? co : latest));
};

// Returns the banner label for an account's change orders, or undefined when no banner should show
// (no CO, or the latest CO has reached a resolved terminal state).
export const resolveChangeOrderBannerLabel = (
  changeOrders: ChangeOrderDocument[] | undefined,
): string | undefined => {
  const mostRelevant = getMostRelevantChangeOrder(changeOrders);
  return mostRelevant ? CHANGE_ORDER_BANNER_LABELS[mostRelevant.status] : undefined;
};
